import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const OrderFulfillmentCost = () => {
  // State variables for Fulfillment Cost calculation
  const [totalExpenses, setTotalExpenses] = useState(0); // Total fulfillment expenses
  const [numberOfOrders, setNumberOfOrders] = useState(0); // Number of orders fulfilled
  const [fulfillmentCost, setFulfillmentCost] = useState(0); // Calculated fulfillment cost

  // Event handlers to update state values
  const handleTotalExpensesChange = (e) =>
    setTotalExpenses(parseFloat(e.target.value) || 0);
  const handleNumberOfOrdersChange = (e) =>
    setNumberOfOrders(parseInt(e.target.value) || 0);

  // useEffect hook to calculate fulfillment cost when any input changes
  useEffect(() => {
    if (numberOfOrders > 0) {
      const cost = totalExpenses / numberOfOrders;
      setFulfillmentCost(cost);
    } else {
      setFulfillmentCost(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [totalExpenses, numberOfOrders]);

  return (
    <Layout
      title="Order Fulfillment Cost Calculator"
      path="order-fulfillment-cost"
    >
      <section className="section-padding" id="fulfillment-cost-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Fulfillment Cost Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">
                  Order Fulfillment Cost Calculator
                </h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Total Fulfillment Expenses Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="totalExpenses" className="form-label">
                        Total Fulfillment Expenses (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Total Fulfillment Expenses:</strong> The total cost incurred for order fulfillment, including storage, packaging, shipping, and handling."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="totalExpenses"
                        placeholder="Enter total fulfillment expenses"
                        value={totalExpenses || ""}
                        onChange={handleTotalExpensesChange}
                      />
                    </div>

                    {/* Number of Orders Fulfilled Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="numberOfOrders" className="form-label">
                        Number of Orders Fulfilled
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Number of Orders Fulfilled:</strong> The total number of customer orders processed and completed during the period."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="numberOfOrders"
                        placeholder="Enter number of orders fulfilled"
                        value={numberOfOrders || ""}
                        onChange={handleNumberOfOrdersChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Fulfillment Cost Per Order:{" "}
                        <span className="fw-bold ms-3">
                          {fulfillmentCost === 0
                            ? "0.00"
                            : fulfillmentCost.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                          ₹
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Order Fulfillment Cost Calculator
                </h3>

                {/* What is an Order Fulfillment Cost Calculator? */}
                <h4 className="mt-3 mb-3">
                  What is an Order Fulfillment Cost Calculator?
                </h4>
                <p>
                  The **Order Fulfillment Cost Calculator** helps businesses
                  determine the cost of fulfilling each order by dividing total
                  fulfillment expenses by the number of orders fulfilled. It
                  provides insights into cost efficiency in the fulfillment
                  process.
                </p>

                {/* Why Use an Order Fulfillment Cost Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use an Order Fulfillment Cost Calculator?
                </h4>
                <p>
                  This calculator is essential for understanding and reducing
                  order fulfillment costs. It supports informed decision-making
                  in pricing, budgeting, and logistics to enhance profitability.
                </p>

                {/* Benefits of Using an Order Fulfillment Cost Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using an Order Fulfillment Cost Calculator
                </h4>
                <ul>
                  <li>
                    <strong>Helps Reduce Costs:</strong> Identifies
                    inefficiencies in the fulfillment process and guides
                    cost-saving measures.
                  </li>
                  <li>
                    <strong>Optimizes Logistics:</strong> Provides insights into
                    warehousing, packing, and shipping expenses.
                  </li>
                  <li>
                    <strong>Supports Pricing Decisions:</strong> Ensures that
                    product pricing covers fulfillment costs and improves
                    margins.
                  </li>
                  <li>
                    <strong>Improves Budgeting:</strong> Assists in planning for
                    fulfillment expenses in business budgets.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use an Order Fulfillment Cost Calculator?
                </h4>
                <p>This calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>E-commerce Businesses:</strong> Helps online
                    retailers manage and optimize fulfillment costs.
                  </li>
                  <li>
                    <strong>Retail Companies:</strong> Supports brick-and-mortar
                    stores with inventory and delivery management.
                  </li>
                  <li>
                    <strong>Logistics Providers:</strong> Useful for analyzing
                    and improving shipping and warehousing operations.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">
                  How can I reduce fulfillment costs?
                </h4>
                <p>
                  To reduce fulfillment costs, you can streamline warehouse
                  operations, negotiate better shipping rates, invest in
                  efficient packing methods, and leverage technology for
                  inventory management.
                </p>

                <h4 className="mt-4 mb-3">
                  Why is tracking fulfillment costs important?
                </h4>
                <p>
                  Tracking fulfillment costs is vital for maintaining
                  profitability, setting competitive prices, and ensuring
                  efficient logistics. It helps identify areas where costs can
                  be minimized without compromising quality.
                </p>

                <h4 className="mt-4 mb-3">
                  What is considered a good fulfillment cost per order?
                </h4>
                <p>
                  A good fulfillment cost per order varies by industry and order
                  volume. However, businesses should aim for the lowest possible
                  cost while maintaining timely and high-quality fulfillment
                  services.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default OrderFulfillmentCost;
